function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '')
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function resolveSnapshot(entity = {}) {
  return entity?.snapshot && typeof entity.snapshot === 'object' ? entity.snapshot : {}
}

function resolveHeader(entity = {}) {
  return resolveSnapshot(entity).header || {}
}

function resolveBuyer(entity = {}) {
  return resolveSnapshot(entity).buyer || {}
}

function resolveSummary(entity = {}) {
  return entity?.summary && typeof entity.summary === 'object' ? entity.summary : {}
}

function resolveLineItems(entity = {}) {
  if (Array.isArray(entity?.lineItems)) {
    return entity.lineItems
  }

  const snapshot = resolveSnapshot(entity)
  return Array.isArray(snapshot?.items) ? snapshot.items : []
}

function resolveEInvoice(entity = {}) {
  return entity?.einvoice && typeof entity.einvoice === 'object' ? entity.einvoice : {}
}

function resolveActionContext(entity = {}) {
  return entity?.actionContext && typeof entity.actionContext === 'object'
    ? entity.actionContext
    : {}
}

function resolveAllowedActions(entity = {}) {
  const actionContext = resolveActionContext(entity)
  if (Array.isArray(actionContext.allowedActions)) {
    return actionContext.allowedActions
  }

  if (Array.isArray(entity.allowedActions)) {
    return entity.allowedActions
  }

  return []
}

function resolveBlockingReasons(entity = {}) {
  const actionContext = resolveActionContext(entity)
  if (Array.isArray(actionContext.blockingReasons)) {
    return actionContext.blockingReasons
  }

  if (Array.isArray(entity.blockingReasons)) {
    return entity.blockingReasons
  }

  return []
}

export function buildActionSubject(entity = {}) {
  console.log(`🚀 ~ buildActionSubject ~ entity:`, entity)
  const snapshot = resolveSnapshot(entity)
  const header = resolveHeader(entity)
  const buyer = resolveBuyer(entity)
  const summary = resolveSummary(entity)
  const einvoice = resolveEInvoice(entity)
  const allowedActions = resolveAllowedActions(entity)
  const blockingReasons = resolveBlockingReasons(entity)

  return {
    salesInvoiceId: firstDefined(entity?.salesInvoiceId, snapshot?.source?.salesInvoiceId, null),
    saleType: firstDefined(entity?.saleType, snapshot?.lane, null),
    status: firstDefined(entity?.status, null),
    taxStatus: firstDefined(entity?.taxStatus, einvoice?.taxStatus, null),
    workState: firstDefined(entity?.workState, einvoice?.workState, header?.workState, null),
    billType: firstDefined(entity?.billType, null),
    billTypeName: firstDefined(entity?.billTypeName, null),
    billMode: firstDefined(entity?.billMode, null),
    sourceChannel: firstDefined(entity?.sourceChannel, null),
    contractInvalid: entity?.contractInvalid === true,
    billNumber: Object.prototype.hasOwnProperty.call(entity, 'billNumber')
      ? (entity.billNumber ?? null)
      : null,
    saleInvoiceCode: firstDefined(entity?.billNumber, entity?.saleInvoiceCode, null),
    invoiceNo: firstDefined(entity?.invoiceNo, einvoice?.invoiceNo, header?.invoiceNo, null),
    pattern: firstDefined(entity?.pattern, einvoice?.pattern, header?.pattern, null),
    serial: firstDefined(entity?.serial, einvoice?.serial, header?.serial, null),
    invoiceDate: firstDefined(entity?.invoiceDate, header?.invoiceDate, null),
    buyerName: firstDefined(entity?.buyerName, buyer?.buyerName, null),
    buyerTaxCode: firstDefined(entity?.buyerTaxCode, buyer?.buyerTaxCode, null),
    buyerEmail: firstDefined(entity?.buyerEmail, buyer?.buyerEmail, null),
    buyerPhone: firstDefined(entity?.buyerPhone, buyer?.buyerPhone, null),
    buyerAddress: firstDefined(entity?.buyerAddress, buyer?.buyerAddress, null),

    buyerIdCard: firstDefined(entity?.buyerIdCard, null),
    paymentMethod: firstDefined(entity?.paymentMethod, header?.paymentMethod, null),
    operationType: firstDefined(
      entity?.operationType,
      einvoice?.operationType,
      header?.operationType,
      null,
    ),
    amountInWords: firstDefined(entity?.amountInWords, summary?.amountInWords, null),
    key: firstDefined(entity?.key, einvoice?.invoiceKey, null),
    keySearch: firstDefined(entity?.keySearch, einvoice?.keySearch, null),
    branchName: firstDefined(entity?.branchName, null),
    totalAmount: toNumber(firstDefined(summary?.totalAmount, entity?.totalAmount, 0), 0),
    feedbackContent: firstDefined(entity?.feedbackContent, einvoice?.lastError, null),
    fieldSemantics: entity?.fieldSemantics || {},
    readiness: entity?.readiness || {},
    readinessSummary: entity?.readinessSummary || {},
    allowedActions,
    blockingReasons,
    actionContext: {
      ...resolveActionContext(entity),
      allowedActions,
      blockingReasons,
    },
    artifacts: entity?.artifacts || {},
    snapshot: entity?.snapshot || null,
    summary: entity?.summary || null,
    einvoice: entity?.einvoice || null,
  }
}

export function buildDraftEditorSeed(entity = {}) {
  const subject = buildActionSubject(entity)
  const header = resolveHeader(entity)
  const summary = resolveSummary(entity)

  return {
    ...subject,
    paymentMethod: firstDefined(entity?.paymentMethod, header?.paymentMethod, null),
    currency: firstDefined(entity?.currency, summary?.currency, header?.currency, 'VND'),
    exchangeRate: firstDefined(entity?.exchangeRate, summary?.exchangeRate, null),
    lineItems: resolveLineItems(entity),
  }
}

export function buildDetailViewModel(entity = {}) {
  const subject = buildActionSubject(entity)
  const summary = resolveSummary(entity)
  const einvoice = resolveEInvoice(entity)

  return {
    ...subject,
    lineItems: resolveLineItems(entity),
    summary: entity?.summary || summary,
    relations: Array.isArray(entity?.relations) ? entity.relations : [],
    reasonCode: firstDefined(entity?.reasonCode, einvoice?.reasonCode, null),
    providerMessage: firstDefined(entity?.providerMessage, einvoice?.lastError, null),
  }
}
